import ClearIcon from '@mui/icons-material/Clear'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { FiSave } from 'react-icons/fi'
import { IOrderListExcel } from '..'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../components/SelectSearch'
import {
  DATE_FORMAT_INTERNATIONAL,
  ONLY_DATE_FORMAT,
} from '../../../../../constants/global'
import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { IAddressThailand } from '../../../../../types/address'
import { useRemoteArea } from '../../../../../hooks/useRemoteArea'
import { useUser } from '../../../../../hooks/useUser'
import { AllowShippingProviderLists } from '../../../../../utils/provider'
import { IRemotesArea } from '../../../../../types/remote-area'
import { message } from 'antd'
import { cleanPhone } from '../../../../../utils'

interface Props {
  order: IOrderListExcel | null
  key: number | null
  isOpen: boolean
  onAdd: (value: IOrderListExcel, key: number) => void
  onClose: (visible: boolean) => void
}

const initRemoteAdrea = {
  remote: {
    flash: false,
    kerry: false,
    jnt: false,
    spx: false,
    thaipost: false,
  },
  toure: {
    flash: false,
    kerry: false,
    jnt: false,
    spx: false,
    thaipost: false,
  },
}

export const OrderFormImport = (props: Props) => {
  const { onAdd, isOpen, onClose, order, key } = props
  const { merchant } = useUser()
  const [data, setData] = useState<IOrderListExcel | null>(null)
  const [searchAddress, setSearchAddress] = useState<string>('')
  const [remoteArea, setRemoteArea] = useState<any>(initRemoteAdrea)

  const remoteAreaData = useRemoteArea()

  const flashProvider = AllowShippingProviderLists(merchant).find(
    (provider) => provider.value === 'flash',
  )

  useEffect(() => {
    if (order) {
      setData(order)
    }
  }, [order])

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })

  const handleCheckRemoteArea = useCallback(
    async (zipcode: string, subDistrict: string, initData?: any) => {
      if (!zipcode || !subDistrict) {
        return
      }

      if (!flashProvider) {
        return
      }
      try {
        const isRemote = remoteAreaData?.data?.remotes.find(
          (remote) =>
            remote.subdistrict === subDistrict && remote.zipcode === zipcode,
        )
        const isTour = remoteAreaData?.data?.tours.find(
          (remote) =>
            remote.subdistrict === subDistrict && remote.zipcode === zipcode,
        )
        setRemoteArea({
          ...remoteArea,
          remote: {
            ...remoteArea.remote,
            flash: !!isRemote,
          },
          tour: {
            ...remoteArea.toure,
            flash: !!isTour,
          },
        })
        const prevData = initData ? initData : data
        setData({
          ...prevData!,
          isRemote: !!isRemote,
          isToure: !!isTour,
        })
      } catch (e) {}
    },
    [remoteArea, merchant, data],
  )

  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  const handleSubmit = () => {
    const phone = data?.phone || ''
    if (!phone) {
      message.error('กรุณากรอกหมายเลขโทรศัพท์')
      return
    }
    onAdd(
      {
        ...data!,
        phone: cleanPhone(phone),
      },
      data?.key || 0,
    )
    onClose(false)
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => onClose(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '600px!important',
          },
        }}
      >
        <DialogTitle>
          แก้ไขรายละเอียด {data?.product || '-'} ลำดับที่{' '}
          {Number(data?.key ?? 0) + 1}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="รายการสินค้า"
                name="product"
                value={data?.product || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    product: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="จำนวน"
                name="quantity"
                value={data?.quantity || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    quantity: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                size="small"
                fullWidth
                label="สินค้าฟรี 0=ไม่ 1=ใช่"
                name="free"
                value={data?.free || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    free: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                size="small"
                fullWidth
                label="ส่วนลด"
                name="discount"
                value={data?.discount || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    discount: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                size="small"
                fullWidth
                label="ขนาด"
                name="size"
                value={data?.size || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    size: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                size="small"
                fullWidth
                label="ราคารวม"
                name="total"
                value={data?.total || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    total: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                size="small"
                fullWidth
                label="ชื่อผู้รับ"
                name="name"
                value={data?.name || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    name: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                size="small"
                fullWidth
                label="โทร"
                name="phone"
                value={data?.phone || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    phone: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                size="small"
                fullWidth
                label="ที่อยู่"
                name="address"
                value={data?.address || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    address: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <SelectSearch<IAddressThailand>
                label={'ค้นหาที่อยู่'}
                onSelect={(e) => {
                  const init = {
                    ...data!,
                    subdistrict: e?.value.subDistrict || '',
                    district: e?.value.district || '',
                    province: e?.value.province || '',
                    zipcode: e?.value.zipcode || '',
                  }
                  setData(init)
                  handleCheckRemoteArea(
                    e?.value.zipcode ?? '',
                    e?.value.subDistrict ?? '',
                    init,
                  )
                }}
                search={searchAddress}
                onSearch={handleSearchAddress}
                options={
                  addressList.data?.map((address: IAddressThailand) => ({
                    label: `${address.subDistrict} ${address.district} ${address.province} ${address.zipcode}`,
                    value: address,
                  })) || ([] as IOptionSelect<IAddressThailand>[])
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="ตำบล"
                name="subdistrict"
                value={data?.subdistrict || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    subdistrict: e.target.value,
                  })
                }}
                onBlur={() => {
                  handleCheckRemoteArea(
                    data?.zipcode ?? '',
                    data?.subdistrict ?? '',
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="อำเภอ"
                name="district"
                value={data?.district || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    district: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="จังหวัด"
                name="province"
                value={data?.province || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    province: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="รหัสไปรษณีย์"
                name="zipcode"
                value={data?.zipcode || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    zipcode: e.target.value,
                  })
                }}
                onBlur={() => {
                  handleCheckRemoteArea(
                    data?.zipcode ?? '',
                    data?.subdistrict ?? '',
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              {Object.keys(remoteArea.remote).map((key: string) => {
                if (remoteArea.remote?.[key]) {
                  return (
                    <Typography fontSize={14} color={'error'}>
                      ** ที่อยู่นี้เป็นพื้นที่ห่างไกลของ {key.toUpperCase()} **
                    </Typography>
                  )
                }
                if (remoteArea.tour?.[key]) {
                  return (
                    <Typography fontSize={14} color={'error'}>
                      ** ที่อยู่นี้เป็นพื้นที่ท่องเที่ยวของ {key.toUpperCase()}{' '}
                      **
                    </Typography>
                  )
                }
                return <></>
              })}
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                size="small"
                fullWidth
                label="โซเชียลมีเดียของลูกค้า"
                name="facebook"
                value={data?.facebook || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    facebook: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="รูปแบบการชำระเงิน"
                name="cod_bank"
                value={data?.cod_bank || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    cod_bank: e.target.value,
                  })
                }}
                select
              >
                <MenuItem value="COD">ชำระปลายทาง</MenuItem>
                <MenuItem value="BANK">ชำระเงินสด</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="โดย"
                name="cod_bank"
                value={data?.actor || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    actor: e.target.value,
                  })
                }}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="ช่องทาง"
                name="channel"
                value={data?.channel || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    channel: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                fullWidth
                label="ผู้ดูแล Telesale"
                name="telesale"
                value={data?.telesale || ''}
                onChange={(e) => {
                  setData({
                    ...data!,
                    telesale: e.target.value,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format={ONLY_DATE_FORMAT}
                  // disableFuture
                  label="วันที่"
                  name="date"
                  value={data?.date ? dayjs(data.date) : null}
                  onChange={(e) =>
                    setData({
                      ...data!,
                      date: e!.format(DATE_FORMAT_INTERNATIONAL),
                    })
                  }
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
              <Grid item>
                <Button
                  startIcon={<FiSave />}
                  color="primary"
                  type="submit"
                  size="large"
                  variant="contained"
                  onClick={handleSubmit}
                >
                  แก้ไขรายละเอียด
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="inherit"
                  size="large"
                  variant="contained"
                  onClick={() => onClose(false)}
                  startIcon={<ClearIcon />}
                >
                  ปิดหน้าต่าง
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default OrderFormImport
